/**
 * The help center (W8-HELP-CENTER, D448, ADR-0079; findings S-01, S-03, S-04 of doc 14a).
 *
 * Pure rules for a ticket a rider, a driver or a partner console user files on their own, and for the conversation that
 * follows it:
 *
 * - **Topics per persona** ({@link HELP_TOPICS}): what each persona may ask about. A rider reports a lost item, a driver
 *   a problem with a passenger or a document; nobody files the other persona's topics. A lost item is always about a ride.
 * - **The server bounds the priority** ({@link selfFiledPriority}, S-04): a caller never raises his own ticket. The
 *   category decides the ceiling (SAFETY is URGENT with immediate routing, a lost item HIGH, the rest NORMAL); a caller
 *   may only ask for LOWER.
 * - **Public replies** ({@link TicketReply}): the requester and the team write in one thread the requester reads. The
 *   agent's INTERNAL notes (D384) never reach it, and the requester never learns which operator answered. A reply of the
 *   requester moves a ticket waiting for him back to the agent, and reopens a resolved one; a closed ticket takes none.
 *   The first public reply of the team is the ticket's FIRST RESPONSE (the SLA clock).
 * - **The SLA clock** ({@link ticketSlaState}) and its **escalation** ({@link escalateIfOverdue}, S-03): a ticket whose
 *   first-response or resolution target passed while it is still open is raised one priority step (once per target), so
 *   it climbs the urgency-ordered queue, with a stable reason code on the case audit.
 *
 * Neutral: personas, categories and codes are data; no country, language or channel provider.
 */
import { err, ok } from '@vello/core';
import { isTicketOpen } from './desk.js';
import { createTicket, SELF_FILED_SUBJECT_CATEGORY, TICKET_PRIORITIES, } from './ticket.js';
/** Who files a ticket from a help center. */
export const HELP_PERSONAS = ['RIDER', 'DRIVER', 'PARTNER'];
const topic = (category, ride) => ({ category, rideRequired: ride === 'required', rideAllowed: ride !== 'none' });
/** What each persona may ask about, in the order the apps list them. */
export const HELP_TOPICS = {
    RIDER: [topic('TRIP', 'allowed'), topic('PAYMENT', 'allowed'), topic('LOST_ITEM', 'required'), topic('DRIVER', 'allowed'), topic('ACCOUNT', 'none'), topic('SAFETY', 'allowed')],
    DRIVER: [topic('TRIP', 'allowed'), topic('PAYMENT', 'allowed'), topic('RIDER', 'allowed'), topic('DOCUMENT', 'none'), topic('ACCOUNT', 'none'), topic('SAFETY', 'allowed')],
    PARTNER: [topic('TRIP', 'none'), topic('PAYMENT', 'none'), topic('MERCHANT', 'none'), topic('DRIVER', 'none'), topic('ACCOUNT', 'none'), topic('SAFETY', 'none')],
};
/**
 * The persona of an authenticated realm, or undefined when that realm has no help center. A partner console's
 * operator realm (`MERCHANT`, the store; `BUSINESS`, the desk and the fleet console — S-05) files as `PARTNER`, the
 * same "Falar com a Vello" the rider and the driver get, bounded by `HELP_TOPICS.PARTNER`.
 */
export function helpPersonaOfRealm(realm) {
    if (realm === 'RIDER')
        return 'RIDER';
    if (realm === 'DRIVER')
        return 'DRIVER';
    if (realm === 'MERCHANT' || realm === 'BUSINESS')
        return 'PARTNER';
    return undefined;
}
/** The ceiling of a self-filed ticket's priority, by category (S-04). */
export function categoryPriorityCeiling(category) {
    if (category === 'SAFETY')
        return 'URGENT';
    if (category === 'LOST_ITEM')
        return 'HIGH';
    return 'NORMAL';
}
/**
 * The priority a self-filed ticket gets (S-04): the category's ceiling, or LOWER when the caller asked for lower. A
 * requested priority above the ceiling (or not a priority at all) is ignored — the server decides, never the body.
 */
export function selfFiledPriority(category, requested) {
    const ceiling = categoryPriorityCeiling(category);
    if (typeof requested !== 'string' || !TICKET_PRIORITIES.includes(requested))
        return ceiling;
    const rank = (p) => TICKET_PRIORITIES.indexOf(p);
    return rank(requested) < rank(ceiling) ? requested : ceiling;
}
const fail = (code, message) => err({ code, message });
/** The longest message or reply (characters). */
export const SUPPORT_MESSAGE_MAX_LENGTH = 2000;
/** A public message's text, trimmed, 1–2 000 characters. */
export function validateSupportMessage(raw) {
    const body = typeof raw === 'string' ? raw.trim() : '';
    if (body === '' || body.length > SUPPORT_MESSAGE_MAX_LENGTH)
        return fail('SUPPORT_MESSAGE_INVALID', `a message has 1 to ${SUPPORT_MESSAGE_MAX_LENGTH} characters`);
    return ok(body);
}
/**
 * A ticket the requester files on his own: the category must be one of his persona's topics, a lost item names a ride,
 * and the priority is the server's (S-04). A SAFETY ticket keeps its immediate routing (`createTicket`). With a `subject` (D637)
 * the category is the subject's and the ticket records what it is about; a subject ticket is never about a ride too.
 */
export function openSelfFiledTicket(input, now) {
    if (input.subject !== undefined) {
        if (input.rideId !== undefined)
            return fail('SUPPORT_RIDE_NOT_ALLOWED', 'a ticket about an order, a delivery or a payment is not about a ride');
        const category = SELF_FILED_SUBJECT_CATEGORY[input.subject.kind];
        const base = createTicket({ ticketId: input.ticketId, category, priority: selfFiledPriority(category, input.requestedPriority), requesterId: input.requesterId, ...(input.sla !== undefined ? { sla: input.sla } : {}) }, now);
        return ok({ ...base, subject: { kind: input.subject.kind, id: input.subject.id } });
    }
    const t = HELP_TOPICS[input.persona].find((x) => x.category === input.category);
    if (t === undefined)
        return fail('SUPPORT_CATEGORY_NOT_OFFERED', `this help center offers ${HELP_TOPICS[input.persona].map((x) => x.category).join('|')}`);
    if (t.rideRequired && input.rideId === undefined)
        return fail('SUPPORT_RIDE_REQUIRED', `a ${t.category} ticket names one of your rides`);
    if (!t.rideAllowed && input.rideId !== undefined)
        return fail('SUPPORT_RIDE_NOT_ALLOWED', `a ${t.category} ticket is not about a ride`);
    const priority = selfFiledPriority(t.category, input.requestedPriority);
    return ok(createTicket({
        ticketId: input.ticketId,
        category: t.category,
        priority,
        requesterId: input.requesterId,
        ...(input.rideId !== undefined ? { rideId: input.rideId } : {}),
        ...(input.sla !== undefined ? { sla: input.sla } : {}),
    }, now));
}
/** Whether a ticket was filed by its requester (the help center) rather than opened by an agent for a caller (D384). */
export function isSelfFiled(ticket) {
    return ticket.openedBy === undefined;
}
// ---- public replies -------------------------------------------------------------------------------------------------
export const REPLY_AUTHORS = ['REQUESTER', 'AGENT'];
/** How many public replies the requester may write on one ticket (a bound against a flood; the team is not a chat). */
export const REQUESTER_REPLY_LIMIT = 30;
/** The ticket after the requester answered (S-01). */
export function applyRequesterReply(ticket, now) {
    if (!isSelfFiled(ticket))
        return fail('SUPPORT_REPLY_NOT_AVAILABLE', 'this ticket is handled by phone');
    if (ticket.status === 'CLOSED')
        return fail('SUPPORT_TICKET_CLOSED', 'this ticket is closed; open a new one');
    // Waiting for the customer → back to the agent who waits; resolved → reopened; otherwise it stays where it is.
    const status = ticket.status === 'PENDING' ? 'ASSIGNED' : ticket.status === 'RESOLVED' ? 'REOPENED' : ticket.status;
    return ok({ ticket: { ...ticket, status, updatedAt: now }, changed: true });
}
/**
 * The ticket after an agent's public reply (S-01): open, assigned to that agent (the desk's work rule), and filed by its
 * requester (a ticket an agent opened for a caller is answered on the call). The first one is the FIRST RESPONSE.
 */
export function applyAgentReply(ticket, agentId, now) {
    if (!isSelfFiled(ticket))
        return fail('SUPPORT_REPLY_NOT_AVAILABLE', 'this ticket was opened for a caller; answer on the call');
    if (!isTicketOpen(ticket))
        return fail('TICKET_NOT_OPEN', `the ticket is ${ticket.status}`);
    if (ticket.assignedAgent !== agentId)
        return fail('TICKET_NOT_ASSIGNED_TO_CALLER', 'assign the ticket to yourself first');
    return ok({ ticket: { ...ticket, updatedAt: now, ...(ticket.firstRespondedAt === undefined ? { firstRespondedAt: now } : {}) }, changed: true });
}
/** Where a ticket stands against its SLA at `now`. */
export function ticketSlaState(ticket, now) {
    const t = Date.parse(now);
    const open = isTicketOpen(ticket);
    const firstResponse = ticket.firstRespondedAt !== undefined ? 'MET' : !open ? 'MET' : t > Date.parse(ticket.sla.firstResponseDue) ? 'BREACHED' : 'RUNNING';
    const resolution = !open ? 'MET' : t > Date.parse(ticket.sla.resolutionDue) ? 'BREACHED' : 'RUNNING';
    const running = [firstResponse === 'RUNNING' ? ticket.sla.firstResponseDue : undefined, resolution === 'RUNNING' ? ticket.sla.resolutionDue : undefined]
        .filter((x) => x !== undefined)
        .sort();
    return {
        firstResponse,
        resolution,
        overdue: firstResponse === 'BREACHED' || resolution === 'BREACHED',
        ...(running[0] !== undefined ? { nextDueAt: running[0] } : {}),
        escalations: ticket.escalations?.length ?? 0,
    };
}
const bump = (p) => TICKET_PRIORITIES[Math.min(TICKET_PRIORITIES.indexOf(p) + 1, TICKET_PRIORITIES.length - 1)];
/**
 * Escalate an open ticket whose target passed (S-03): one priority step up (URGENT stays URGENT), once per target (first
 * response, then resolution), with the reason code the case audit records. The SLA due times the caller was promised do
 * not move. Anything else is unchanged.
 */
export function escalateIfOverdue(ticket, now) {
    if (!isTicketOpen(ticket))
        return { ticket, changed: false };
    const state = ticketSlaState(ticket, now);
    const done = new Set((ticket.escalations ?? []).map((e) => e.kind));
    const kind = state.firstResponse === 'BREACHED' && !done.has('FIRST_RESPONSE') ? 'FIRST_RESPONSE' : state.resolution === 'BREACHED' && !done.has('RESOLUTION') ? 'RESOLUTION' : undefined;
    if (kind === undefined)
        return { ticket, changed: false };
    const escalation = {
        kind,
        reasonCode: kind === 'FIRST_RESPONSE' ? 'SLA_FIRST_RESPONSE_BREACHED' : 'SLA_RESOLUTION_BREACHED',
        at: now,
        fromPriority: ticket.priority,
        toPriority: bump(ticket.priority),
    };
    return { ticket: { ...ticket, priority: escalation.toPriority, escalations: [...(ticket.escalations ?? []), escalation], updatedAt: now }, changed: true, escalation };
}
/** The queue order (S-02): most urgent first, then the earliest running due time, then the oldest. */
export function compareQueueOrder(a, b) {
    const rank = (t) => TICKET_PRIORITIES.length - 1 - TICKET_PRIORITIES.indexOf(t.priority);
    if (rank(a) !== rank(b))
        return rank(a) - rank(b);
    const due = (t) => (t.firstRespondedAt === undefined && t.sla.firstResponseDue < t.sla.resolutionDue ? t.sla.firstResponseDue : t.sla.resolutionDue);
    const d = due(a).localeCompare(due(b));
    if (d !== 0)
        return d;
    return a.createdAt.localeCompare(b.createdAt);
}
//# sourceMappingURL=help-center.js.map